import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown } from "lucide-react";

import { auth as authApi } from "@/lib/api";

/**
 * The little avatar + name + role block at the bottom of the sidebar.
 * Reads the real user from /auth/me. Derives initials from the email when
 * `name` isn't surfaced (Cognito access tokens don't always carry it).
 */
export function SidebarUser() {
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => authApi.me(),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  if (!meQuery.isSuccess) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-surface-elevated animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="h-3 w-24 bg-surface-elevated rounded animate-pulse mb-1" />
          <div className="h-2 w-16 bg-surface-elevated rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const email = meQuery.data.email ?? "user";
  const display = displayNameFromEmail(email);
  const initials = initialsFromEmail(email);
  const role = humanRole(meQuery.data.role);
  const staff = meQuery.data.is_dravian_staff;

  return (
    <div className="flex items-center gap-2.5">
      <div className="h-8 w-8 rounded-full bg-primary/20 text-primary grid place-items-center text-[11px] font-semibold">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium truncate" title={email}>
          {display}
        </div>
        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
          {role}
          {staff && (
            <span className="text-primary border border-primary/40 bg-primary/10 px-1 rounded-sm uppercase tracking-wider text-[9px]">
              staff
            </span>
          )}
        </div>
      </div>
      <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
  );
}

function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  // "santiago.carvajal" → "Santiago Carvajal", "alice_castro" → "Alice Castro"
  return local
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

function initialsFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const parts = local.replace(/[._-]+/g, " ").split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? email.slice(0, 2)).toUpperCase();
}

function humanRole(role: string | undefined): string {
  switch (role) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "analyst":
      return "Analyst";
    case "viewer":
      return "Viewer";
    default:
      return "Member";
  }
}
